import React, { useState, useEffect } from "react";
import Api from "../../components/api/Api";
import { Col, Container, Row } from "react-bootstrap";
import { Formik, Form, Field, ErrorMessage } from "formik";
import Notification, { showToast } from "../../../components/common/notification/Notification";
import { useNavigate, useParams } from "react-router-dom";
import Spinner from "../../../components/common/spinner/Spinner";
import SingleDetailHeader from "../../components/singleDetail/SingleDetailHeader";
import BreadCrumb from "../../components/breadcrumb/BreadCrumb";

const EditAssociatePartner = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [initialValues, setInitialValues] = useState(null);
  const [categories, setCategories] = useState([]);

  const getPartnerApi = Api({
    url: `admin/associate/partner/${id}`,
  });

  const updatePartnerApi = Api({
    url: `admin/associate/partner/edit`,
  });

  const getCategoryApi = Api({
    url: "admin/associate/partner/categories",
  });

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Fetch Categories
        const catResponse = await getCategoryApi.fetchData(1);
        let fetchedCats = [];
        if (catResponse && catResponse.data) {
          if (catResponse.data.associatePartnerCategories) {
            fetchedCats = catResponse.data.associatePartnerCategories;
          } else if (catResponse.data.associatePartnerCategory) {
            fetchedCats = Array.isArray(catResponse.data.associatePartnerCategory) ? catResponse.data.associatePartnerCategory : [catResponse.data.associatePartnerCategory];
          } else if (Array.isArray(catResponse.data)) {
            fetchedCats = catResponse.data;
          }
        }
        setCategories(fetchedCats.filter(cat => cat.active));

        // Fetch Partner details
        if (id) {
          const partnerResponse = await getPartnerApi.fetchDataBySingleId();
          if (partnerResponse && partnerResponse.data && partnerResponse.data.statusType === "SUCCESS") {
            const partner = partnerResponse.data.associatedPartner;
            setInitialValues({
              name: partner.name || "",
              categoryId: partner.category?.id || "",
              phone_number: partner.phone_number || "",
              city: partner.city || "",
              state: partner.state || "",
              email_address: partner.email_address || "",
              website: partner.website || "",
            });
          } else {
            showToast("Failed to fetch partner details", { type: "error" });
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setSubmitting(true);

      const data = {
        id: Number(id),
        name: values.name,
        category: {
          id: Number(values.categoryId)
        },
        phone_number: values.phone_number,
        city: values.city,
        state: values.state,
        email_address: values.email_address,
        website: values.website
      };

      const response = await updatePartnerApi.editData(data);
      if (response && response.data && response.data.statusType === "SUCCESS") {
        showToast("Associate Partner updated successfully", { type: "success" });
        setTimeout(() => {
          navigate("/associate-partners");
        }, 3000);
      } else {
        showToast(response?.data?.text || "Failed to update partner", { type: "error" });
      }
    } catch (error) {
      console.error(error);
      showToast("Error Occurred", { type: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const breadcrumbItems = [
    { label: "", link: "/admin", icon: "fa-solid fa-house" },
    { label: "Partner Network", link: "/partners" },
    { label: "Associate Partners", link: "/associate-partners" },
    { label: "Edit Associate Partner", link: "", active: true },
  ];

  return (
    <>
      <Notification />
      <Container fluid style={{ marginTop: "2rem", padding: "0rem 4rem" }}>
        <BreadCrumb items={breadcrumbItems} />
        <Row>
          <Col lg={12} md={12} sm={12}>
            <SingleDetailHeader
              header="Edit Associate Partner"
              description={`Update details for associate partner ID: ${id}`}
            />
            {initialValues ? (
              <Formik initialValues={initialValues} enableReinitialize={true} onSubmit={handleSubmit}>
                {({ isSubmitting }) => (
                  <Form>
                    <Row>
                      <Col md={6}>
                        <div className="mb-3">
                          <label htmlFor="name" className="form-label">Name</label>
                          <Field type="text" className="form-control" id="name" placeholder="Enter partner name" name="name" required />
                          <ErrorMessage name="name" component="div" className="text-danger" />
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-3">
                          <label htmlFor="categoryId" className="form-label">Category</label>
                          <Field as="select" className="form-control form-select" id="categoryId" name="categoryId" required>
                            <option value="">Select Category</option>
                            {categories.map((cat) => (
                              <option key={cat.id} value={cat.id}>
                                {cat.name}
                              </option>
                            ))}
                          </Field>
                          <ErrorMessage name="categoryId" component="div" className="text-danger" />
                        </div>
                      </Col>
                    </Row>
                    
                    <Row>
                      <Col md={6}>
                        <div className="mb-3">
                          <label htmlFor="phone_number" className="form-label">Phone Number</label>
                          <Field type="text" className="form-control" id="phone_number" placeholder="Enter phone number" name="phone_number" required />
                          <ErrorMessage name="phone_number" component="div" className="text-danger" />
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-3">
                          <label htmlFor="email_address" className="form-label">Email Address</label>
                          <Field type="email" className="form-control" id="email_address" placeholder="Enter email address" name="email_address" required />
                          <ErrorMessage name="email_address" component="div" className="text-danger" />
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <div className="mb-3">
                          <label htmlFor="city" className="form-label">City</label>
                          <Field type="text" className="form-control" id="city" placeholder="Enter city" name="city" required />
                          <ErrorMessage name="city" component="div" className="text-danger" />
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-3">
                          <label htmlFor="state" className="form-label">State</label>
                          <Field type="text" className="form-control" id="state" placeholder="Enter state" name="state" required />
                          <ErrorMessage name="state" component="div" className="text-danger" />
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={12}>
                        <div className="mb-3">
                          <label htmlFor="website" className="form-label">Website</label>
                          <Field type="url" className="form-control" id="website" placeholder="https://example.com" name="website" required />
                          <ErrorMessage name="website" component="div" className="text-danger" />
                        </div>
                      </Col>
                    </Row>

                    <button type="submit" className="btn btn-primary mt-4" style={{ marginBottom: "10rem" }} disabled={isSubmitting}>
                      {isSubmitting ? <Spinner loading={true} /> : "Update Associate Partner"}
                    </button>
                  </Form>
                )}
              </Formik>
            ) : (
              <div className="text-center my-5">
                <Spinner loading={true} />
                <p className="mt-3">Loading partner details...</p>
              </div>
            )}
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default EditAssociatePartner;
